import { zodOptional } from '@studio/utils/zod';
import { z } from 'zod';
import { IElement, ZElement, ZElementProperty } from './element';
import { ImageLibraryAsset, ZImageLibraryAsset } from './image-asset';
import { IWidgetLibraryAsset, ZWidgetLibraryAsset } from './widget-asset';
import { serializePropertyValue } from '@creative/serialization/property-value-serializer';
import { VideoLibraryAsset, ZVideoLibraryAsset } from './video-asset';

export const ZBrandElement = ZElement.extend({
    parentFolderId: zodOptional(z.string()),
    created: z.string(),
    modified: zodOptional(z.string()),
    properties: z.array(ZElementProperty.omit({ id: true }))
}).omit({ parentId: true });

export type BrandElementDto = z.infer<typeof ZBrandElement>;

export const ZBrandFolder = z.object({
    id: z.string(),
    name: z.string(),
    parentFolderId: zodOptional(z.string())
});
const ZNewBrandFolder = ZBrandFolder.omit({ id: true });

export type Folder = z.infer<typeof ZBrandFolder>;
export type NewFolder = z.infer<typeof ZNewBrandFolder>;

export const ZBrandLibraryDto = z.object({
    id: z.string(),
    brandId: z.string(),
    elements: z.array(ZBrandElement),
    folders: z.array(ZBrandFolder),
    images: z.array(ZImageLibraryAsset),
    videos: z.array(ZVideoLibraryAsset),
    widgets: z.array(ZWidgetLibraryAsset)
});

export type BrandLibraryDto = z.infer<typeof ZBrandLibraryDto>;

export interface IBrandLibrary {
    id: string;
    brandId: string;
    elements: IBrandLibraryElement[];
    folders: Folder[];
    images: ImageLibraryAsset[];
    videos: VideoLibraryAsset[];
    widgets: IWidgetLibraryAsset[];
}

export interface IBrandLibraryElement
    extends Omit<IElement, 'parentId' | 'properties'>,
        Omit<Pick<IElement, 'properties'>, 'id'> {
    created: string;
    modified?: string;
    parentFolderId?: string;
}

const ZCreationElement = ZBrandElement.omit({
    id: true,
    created: true,
    modified: true
});

const ZUpdateElement = ZBrandElement.omit({
    created: true,
    modified: true
});

type ElementCreateOrUpdate = z.infer<typeof ZCreationElement> | z.infer<typeof ZUpdateElement>;

function serializeBrandlibraryElementProperties<Element extends ElementCreateOrUpdate>(
    element: Element
): Element {
    element.properties.forEach(
        property => (property.value = serializePropertyValue(property.name, property.value))
    );

    return element;
}

export const ZElementCreationVariables = z.object({
    element: ZCreationElement.transform(serializeBrandlibraryElementProperties),
    originalElementId: zodOptional(z.string()),
    brandId: z.string()
});

export const ZElementUpdateVariables = z.object({
    element: ZUpdateElement.transform(serializeBrandlibraryElementProperties),
    brandId: z.string()
});

export const ZCreateFolderVariables = z.object({
    name: z.string(),
    brandLibraryId: z.string(),
    parentFolderId: zodOptional(z.string())
});

export type BrandLibraryQueryResult = {
    brandLibrary: BrandLibraryDto;
};
export type CreateElementInBrandLibraryVariables = z.infer<typeof ZElementCreationVariables>;
export type UpdateElementInBrandLibraryVariables = z.infer<typeof ZElementUpdateVariables>;
export type CreateBrandLibraryFolderVariables = z.infer<typeof ZCreateFolderVariables>;

export type CreateElementInBrandLibraryResult = { createElementInBrandLibrary: BrandLibraryDto };
export type UpdateElementInBrandLibraryResult = { updateElementInBrandLibrary: BrandLibraryDto };
export type DeleteElementsInBrandLibraryResult = { deleteElementsInBrandLibrary: BrandLibraryDto };
export type CreateBrandLibraryFolderResult = {
    createBrandLibraryFolder: {
        folders: Folder[];
    };
};
